// Build trigger: 2025-12-23-1913
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App as CapacitorApp } from '@capacitor/app'
import { RouterProvider, createRouter } from '@spcc/core'
import { routeTree } from './routeTree.gen'
import { store } from './store'
import './styles/index.css'
import { PRIVACY_POLICY_VERSION } from '@/lib/policy'
import { isNativeApp } from '@/lib/platform'

// Analytics initialization
import {
  analytics,
  ConsoleAdapter,
  NewRelicAdapter,
  CompositeAdapter,
  initWebVitals,
  initRouterAnalytics,
  type AnalyticsConfig,
} from '@spcc/domains/analytics'

// Determine environment
const appEnv = (import.meta.env.VITE_APP_ENV || import.meta.env.MODE) as 'development' | 'staging' | 'production'
const isDeployed = !import.meta.env.DEV // true when running production build (any env)
const isNative = isNativeApp()
const CUSTOMER_PORTAL_SCHEME = 'com.spcc.customer.app:'

// Analytics configuration
const analyticsConfig: AnalyticsConfig = {
  enabled: true,
  debug: import.meta.env.DEV,
  appId: 'customer-portal',
  appVersion: import.meta.env.VITE_APP_VERSION ?? '1.0.0',
  environment: appEnv,
  requireConsent: !isNative,
  autoTrackPageViews: true,
  autoTrackErrors: true,
  trackWebVitals: true,
  trackReduxActions: true,
  piiFields: ['email', 'phone', 'address', 'name', 'password'],
  sampleRate: 1,
  policyVersion: PRIVACY_POLICY_VERSION,
  adapters: [
    {
      type: 'console',
      enabled: import.meta.env.DEV,
      options: { collapsed: true },
    },
    {
      type: 'newrelic',
      enabled: isDeployed && !!import.meta.env.VITE_NEW_RELIC_APP_ID,
      options: {
        licenseKey: import.meta.env.VITE_NEW_RELIC_LICENSE_KEY,
        applicationId: import.meta.env.VITE_NEW_RELIC_APP_ID,
        accountId: import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID,
        trustKey: import.meta.env.VITE_NEW_RELIC_TRUST_KEY,
        environment: appEnv,
        appName: `customer-portal-${appEnv}`,
      },
    },
  ],
}

// Initialize analytics before React renders
const adapter = isDeployed
  ? (() => {
      const composite = new CompositeAdapter([new NewRelicAdapter(), new ConsoleAdapter()])
      // Set specific config for each adapter
      const newRelicConfig = analyticsConfig.adapters.find(a => a.type === 'newrelic')
      const consoleConfig = analyticsConfig.adapters.find(a => a.type === 'console')
      if (newRelicConfig) composite.setAdapterConfig('newrelic', newRelicConfig)
      if (consoleConfig) composite.setAdapterConfig('console', consoleConfig)
      return composite
    })()
  : new ConsoleAdapter()

analytics.initialize(adapter, analyticsConfig)
analytics.setConsent(store.getState().consent.analytics)

// Initialize Web Vitals tracking
initWebVitals({ debug: import.meta.env.DEV })

// Router context type - includes store for loaders
export interface RouterContext {
  store: typeof store
}

// Create the router instance with context
const router = createRouter({
  routeTree,
  context: {
    store,
  },
})

// Initialize router analytics (auto-tracks page views)
initRouterAnalytics(router, { debug: import.meta.env.DEV })

function isScrollableHorizontally(element: Element): boolean {
  const style = window.getComputedStyle(element)
  return style.overflowX === 'auto' || style.overflowX === 'scroll'
}

function resetHorizontalScroll(target: EventTarget | null): void {
  try {
    window.scrollTo({ left: 0, top: window.scrollY })
  } catch {
    // ignore
  }

  if (document.documentElement.scrollLeft !== 0) document.documentElement.scrollLeft = 0
  if (document.body.scrollLeft !== 0) document.body.scrollLeft = 0

  const element = target instanceof Element ? target : null
  if (!element) return

  let current: Element | null = element
  while (current && current !== document.body) {
    if (current.scrollLeft !== 0) current.scrollLeft = 0
    if (isScrollableHorizontally(current) && current.scrollLeft !== 0) current.scrollLeft = 0
    current = current.parentElement
  }
}

function installFocusHorizontalScrollGuard(): void {
  if (typeof window === 'undefined') return
  const handler = (event: Event) => {
    // iOS/Android WebViews can introduce a small scrollLeft when focusing inputs (keyboard resize).
    // Run twice to catch post-layout adjustments.
    requestAnimationFrame(() => resetHorizontalScroll(event.target))
    requestAnimationFrame(() => requestAnimationFrame(() => resetHorizontalScroll(event.target)))
  }
  window.addEventListener('focusin', handler, { passive: true })

  const viewport = window.visualViewport
  if (viewport) {
    viewport.addEventListener('resize', () => resetHorizontalScroll(null), { passive: true })
    viewport.addEventListener('scroll', () => resetHorizontalScroll(null), { passive: true })
  }
}

installFocusHorizontalScrollGuard()

function extractInAppPath(url: string): string | null {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== CUSTOMER_PORTAL_SCHEME) return null
    const path = parsed.pathname.startsWith('/') ? parsed.pathname : `/${parsed.pathname}`
    const hostPrefix = parsed.hostname ? `/${parsed.hostname}` : ''
    return `${hostPrefix}${path}${parsed.search}${parsed.hash}`
  } catch {
    return null
  }
}

function navigateToDeepLink(url: string): void {
  const inAppPath = extractInAppPath(url)
  if (!inAppPath) return

  const [pathWithoutHash, hashFragment] = inAppPath.split('#')
  const [pathname, searchString = ''] = pathWithoutHash.split('?')
  const searchParams = new URLSearchParams(searchString)

  if (pathname === '/sso/handoff') {
    const token = searchParams.get('token') || ''
    void router.navigate({
      to: '/sso/handoff',
      search: { token },
      hash: hashFragment || undefined,
      replace: true,
    })
    return
  }

  window.history.replaceState(null, '', inAppPath)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

async function installNativeDeepLinkRouting(): Promise<void> {
  if (!isNativeApp()) return

  const launchUrl = await CapacitorApp.getLaunchUrl()
  if (launchUrl?.url) {
    navigateToDeepLink(launchUrl.url)
  }

  await CapacitorApp.addListener('appUrlOpen', (event) => {
    if (!event.url) return
    navigateToDeepLink(event.url)
  })
}

void installNativeDeepLinkRouting()

// Register the router instance for type safety
declare module '@spcc/core' {
  interface Register {
    router: typeof router
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
)
